import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { GooglePlayIcon, Apple } from "../../assets/DirectTopUpIcon";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { fetchProviderFaqs } from "../../features/store/providerFaqSlice";
import { QuestionDownIcon, QuestionIcon } from "../../assets/icons";

const TopUpDetails = () => {
  const tabItems = ["Details", "FAQs"];
  const [toggle, setToggle] = useState<string>(tabItems[0] || "Details");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const dispatch = useAppDispatch();
  const location = useLocation();
  const { data: appData } = useAppSelector((state) => state.appData);
  const {
    faqs,
    loading: faqLoading,
    error: faqError,
  } = useAppSelector((state) => state.providerFaqs);

  // Extract the last path segment as the category key
  const path = location.pathname;
  const segments = path.split("/").filter(Boolean); // ["products", "data", "MTN"]
  const formattedProduct =
    segments.length >= 2 ? segments[segments.length - 2] : "";
  const formattedProvider =
    segments.length >= 1 ? segments[segments.length - 1] : "";

  const providers = Array.isArray(appData[formattedProduct]?.providers)
    ? appData[formattedProduct].providers
    : [];

  const provider = providers.find(
    (item: { slug: string }) => item.slug === formattedProvider,
  );

  const providerKey = useMemo(
    () => `${formattedProduct}-${provider?.slug ?? ""}`,
    [formattedProduct, provider?.slug],
  );

  useEffect(() => {
    if (!provider?.slug || !formattedProduct) return;

    dispatch(
      fetchProviderFaqs({
        productKey: formattedProduct,
        providerSlug: provider.slug,
      }),
    );
  }, [provider?.slug, formattedProduct, dispatch]);

  useEffect(() => {
    if (toggle === "FAQs") {
      setShowButton(faqs.length > 5);
    } else if (contentRef.current) {
      setShowButton(contentRef.current.scrollHeight > 220);
    }
  }, [toggle, provider, faqs]);

  return (
    provider && (
      <div className="w-full bg-white dark:bg-black flex flex-col gap-2 lg:gap-4  px-2">
        <img
          src={provider.image_url}
          alt={formattedProvider}
          className="w-full aspect-square object-cover rounded-lg"
        />

        {tabItems.length > 0 && (
          <div className="rounded-lg p-[6px] row-2 bg-[#F4F4F5] dark:bg-BG-dark w-full h-[42px]">
            {tabItems.map((item, index) => (
              <button
                key={index}
                className={`flex-1 py-[6px] px-[6px] bp:px-[10px] w-[140px] bp:w-[153px] h-[34px] text-xs place-content-center ${
                  toggle === item
                    ? "inter-xs-semibold-dark rounded-lg font-inter dark:text-white bg-[#FCFCFD] dark:bg-[#FFFFFF1A]"
                    : "inter-xs-medium-toggleBG-dark dark:text-main-dark"
                }`}
                onClick={() => setToggle(item)}
              >
                {item}
              </button>
            ))}
          </div>
        )}

        <div className="relative">
          {/* The Scrollable/Collapsible Container */}
          <div
            ref={contentRef}
            className={`overflow-hidden transition-all duration-500 ${
              toggle === "Details" && !isExpanded
                ? "max-h-[220px] bp:max-h-none"
                : "max-h-fit"
            }`}
          >
            <div className="p-4">
              {toggle === "Details" ? (
                <div className="flex flex-col px-4 pt-2 dark:text-white">
                  <p className="text-xl font-semibold font-lt">
                    {provider.name}
                  </p>
                  {provider.description && (
                    <div
                      className="text-sm font-satoshi [&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_a]:no-underline"
                      dangerouslySetInnerHTML={{ __html: provider.description }}
                    />
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-2 dark:text-white">
                  <p className="text-xl font-semibold font-lt mb-2">
                    {provider.name} FAQs
                  </p>
                  {faqLoading ? (
                    <p className="text-sm font-satoshi">Loading FAQs...</p>
                  ) : faqError ? (
                    <p className="text-sm font-satoshi text-red-500">
                      {faqError}
                    </p>
                  ) : faqs.length > 0 ? (
                    faqs
                      .slice(0, !isExpanded ? 5 : faqs.length)
                      .map((faq, index) => {
                        const isOpen = openFaqIndex === index;
                        return (
                          <div
                            key={faq.id}
                            className="border border-border-border1 dark:border-main-light rounded-lg bg-white dark:bg-BG-dark overflow-hidden shadow-sm"
                          >
                            <button
                              onClick={() =>
                                setOpenFaqIndex(isOpen ? null : index)
                              }
                              className="w-full flex justify-between items-center p-4 text-start transition-colors"
                            >
                              <span
                                className={`text-sm font-semibold ${isOpen ? "text-pry-600" : "text-tabs dark:text-white"}`}
                              >
                                {faq.question}
                              </span>
                              <div className="flex-shrink-0">
                                {isOpen ? (
                                  <QuestionDownIcon />
                                ) : (
                                  <QuestionIcon />
                                )}
                              </div>
                            </button>

                            {isOpen && (
                              <div className="px-4 pb-4 animate-fadeIn">
                                <div className="h-[1px] bg-border-border1 dark:bg-main-light mb-3 opacity-50" />
                                <div
                                  className="satoshi-base-normal-main-light dark:text-main-dark text-sm leading-relaxed"
                                  dangerouslySetInnerHTML={{
                                    __html: faq.answer,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })
                  ) : (
                    <p className="text-sm font-satoshi">No FAQs available.</p>
                  )}
                </div>
              )}
            </div>

            {/* Gradient Overlay */}
            {showButton && !isExpanded && toggle === "Details" && (
              <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-white dark:from-black to-transparent bp:hidden pointer-events-none" />
            )}
          </div>

          {showButton && (
            <div className="bp:hidden mt-0">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full py-3 text-sm font-bold text-blue-600 dark:text-blue-400 border-t border-gray-100 dark:border-gray-800 flex items-center justify-center gap-1"
              >
                {isExpanded ? (
                  <>
                    Show Less <span className="text-[10px]">▲</span>
                  </>
                ) : (
                  <>
                    Read More & FAQs <span className="text-[10px]">▼</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div>
          {provider.google_url ||
            (provider.google_url && (
              <p className="text-sm font-semibold mb-2 font-lt">Get the game</p>
            ))}
          <div className="flex items-center gap-2">
            {provider.google_url && (
              <a
                href={provider.google_url}
                className="bg-[#000000] flex gap-2 items-center w-full lg:w-[148.5px] h-[44px] dark:border-tabs rounded-md border-[1px] px-2"
              >
                <GooglePlayIcon />
                <div>
                  <p className="text-[8px] bp:text-[6px] lg:text-[8px] xl:text-[10px] text-white">
                    GET IT ON
                  </p>
                  <p className="text-xs bp:text-[7px] lg:text-[9px] xl:text-base text-white">
                    Google Play
                  </p>
                </div>
              </a>
            )}
            {provider.apple_url && (
              <a
                href={provider.apple_url}
                className="bg-[#000000] flex gap-2 items-center w-full lg:w-[148.5px] h-[44px] dark:border-tabs rounded-md border-[1px] px-2"
              >
                <Apple />
                <div>
                  <p className="text-[8px] bp:text-[6px] lg:text-[8px] xl:text-[10px] text-white">
                    Download on the{" "}
                  </p>
                  <p className="text-xs bp:text-[7px] lg:text-[10px] xl:text-base text-white">
                    App Stores
                  </p>
                </div>
              </a>
            )}
          </div>
        </div>
      </div>
    )
  );
};

export default TopUpDetails;
